import type { Step } from "@/components/tour/types";

// ============================================================
// ADMIN PAGE TOURS
// ============================================================

export const commandCenterSteps: Step[] = [
  {
    target: "body",
    content: "Welcome to the Command Center — your central hub for managing all clients and operations. Let's walk through the key features.",
    title: "Command Center",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="stat-cards"]',
    content: "These cards show your key business metrics at a glance: active clients, total MRR, pending tickets, and upgrade requests.",
    title: "Business Metrics",
  },
  {
    target: '[data-tour-id="tabs"]',
    content: "Switch between different views: Client Registry for managing clients, Client Integrations for connections, Payment Methods for billing setup, Revenue Collection for tracking payments, and Fanbasis Setup for payment processing.",
    title: "Navigation Tabs",
  },
  {
    target: '[data-tour-id="client-search"]',
    content: "Search for clients by name or email. Filter by status (Active, Paused, Inactive, All) to find specific accounts.",
    title: "Search & Filter",
  },
  {
    target: '[data-tour-id="client-table"]',
    content: "Your client list shows each client's package, services, client code, and setup status. The colored info icon shows which integrations are connected — hover to see details.",
    title: "Client List",
  },
  {
    target: '[data-tour-id="add-client"]',
    content: "Click here to add a new client. You'll enter their company name, contact info, package type, and services. They'll receive login credentials automatically.",
    title: "Add New Client",
  },
];

export const salesOperationsSteps: Step[] = [
  {
    target: "body",
    content: "Sales Operations is where you manage your call team, track demo performance, and handle sales support. Let's explore each section.",
    title: "Sales Operations",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="team-metrics"]',
    content: "These metrics give you a snapshot of your team's performance: total demos, average conversion rate, number of active closers, and combined weekly targets.",
    title: "Team Performance",
  },
  {
    target: '[data-tour-id="tabs"]',
    content: "Navigate between tabs: Overview shows closer assignments, Demo Metrics shows performance data, Assignments manages closer-to-client pairings, Manage Closers adds/removes team members, Sales Tickets handles support, Closer Requests tracks client requests, and Venture Hills Team manages your setter/closer roster for attribution.",
    title: "Operations Tabs",
  },
];

export const adminDashboardsSteps: Step[] = [
  {
    target: "body",
    content: "The Dashboards page shows high-level analytics across all your clients. Use it to track revenue trends, client distribution, and performance over time.",
    title: "Admin Dashboards",
    placement: "center",
    disableBeacon: true,
  },
];

export const pipelineSteps: Step[] = [
  {
    target: "body",
    content: "The Pipeline view shows all leads and opportunities flowing through your GHL pipeline. Each entry represents a potential demo or deal at various stages.",
    title: "Sales Pipeline",
    placement: "center",
    disableBeacon: true,
  },
];

export const adminReportsSteps: Step[] = [
  {
    target: "body",
    content: "Reports give you a comprehensive view of revenue, demo volume, client activity, and integration sync health. Use the stat cards to spot issues and the tables for detailed records.",
    title: "Admin Reports",
    placement: "center",
    disableBeacon: true,
  },
];

export const rolesPermissionsSteps: Step[] = [
  {
    target: "body",
    content: "Manage your team members and their access levels here. Only Super Admins can access this page.",
    title: "Roles & Permissions",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="role-cards"]',
    content: "Three role levels: Super Admin has full access including billing and team management. Team Admin can manage clients and integrations but not billing. Member has view-only access to basic pages.",
    title: "Role Levels",
  },
  {
    target: '[data-tour-id="add-member"]',
    content: "Invite a new team member by email. They'll receive an email with a link to set their password and activate their account. No need to create passwords manually.",
    title: "Invite Team Members",
  },
  {
    target: '[data-tour-id="member-list"]',
    content: "View all team members, their roles, and status. Use the action menu (three dots) to change roles, send password reset emails, or deactivate accounts.",
    title: "Team Member List",
  },
];

export const supportTicketsSteps: Step[] = [
  {
    target: "body",
    content: "View and manage all client support tickets. Filter by status, search by subject or client name, and click into tickets to respond.",
    title: "Support Tickets",
    placement: "center",
    disableBeacon: true,
  },
];

export const packagesSteps: Step[] = [
  {
    target: "body",
    content: "Compare your Standard and Elite package offerings. See client distribution, feature differences, and performance metrics for each package tier.",
    title: "Packages",
    placement: "center",
    disableBeacon: true,
  },
];

export const onboardingProcessSteps: Step[] = [
  {
    target: "body",
    content: "This page outlines the complete client onboarding journey from initial contact through first demo delivery. Use it as a reference for where each client is in their setup process.",
    title: "Onboarding Process",
    placement: "center",
    disableBeacon: true,
  },
];

export const ventureHillsOverviewSteps: Step[] = [
  {
    target: "body",
    content: "A detailed breakdown of what's included in each Venture Hills package. Use this as a reference when discussing offerings with potential clients.",
    title: "Venture Hills Overview",
    placement: "center",
    disableBeacon: true,
  },
];

export const notificationSettingsSteps: Step[] = [
  {
    target: "body",
    content: "Configure which notifications you receive and how. Toggle Slack and email notifications for different event types like new clients, billing events, and demo updates.",
    title: "Notification Settings",
    placement: "center",
    disableBeacon: true,
  },
];

// ============================================================
// CLIENT PAGE TOURS
// ============================================================

export const dashboardSteps: Step[] = [
  {
    target: "body",
    content: "Welcome to your Dashboard — here's an overview of your demo performance, recent activity, and pipeline metrics. Let's walk through what everything means.",
    title: "Your Dashboard",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="demo-performance"]',
    content: "Your demo performance metrics: total demos scheduled, show rate (percentage who showed up), close rate (percentage that closed), and total revenue from closed deals.",
    title: "Demo Performance",
  },
  {
    target: '[data-tour-id="recent-activity"]',
    content: "A live feed of your latest demo activity — who was the prospect, what happened (completed, no-show, cancelled), and when it occurred.",
    title: "Recent Activity",
  },
  {
    target: '[data-tour-id="upcoming-demos"]',
    content: "Demos scheduled in the next 7 days. Shows the prospect name, date, time, and assigned closer.",
    title: "Upcoming Demos",
  },
  {
    target: '[data-tour-id="pipeline-overview"]',
    content: "Your full lead-to-close pipeline pulled from GHL. Shows how many leads, demos booked, demos completed, and deals closed. Requires GHL connection and stage mapping.",
    title: "Pipeline Overview",
  },
];

export const marketingSteps: Step[] = [
  {
    target: "body",
    content: "Marketing Performance shows data from your connected ad accounts (Google Ads and/or Meta Ads). Metrics sync daily so you can track campaign results alongside your demo pipeline.",
    title: "Marketing Performance",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="key-metrics"]',
    content: "Your top-line metrics: total leads generated, clicks, ad spend, CTR (click-through rate), and pipeline demos (if GHL stage mapping is configured).",
    title: "Key Metrics",
  },
  {
    target: '[data-tour-id="campaign-table"]',
    content: "Individual campaign breakdown showing leads, clicks, spend, and cost-per-lead for each campaign. Click column headers to sort.",
    title: "Campaign Details",
  },
];

export const billingSteps: Step[] = [
  {
    target: "body",
    content: "Billing shows your current week's estimated charges and full billing history. Demos are billed weekly every Sunday. Your first 3 demos are free.",
    title: "Billing",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="billing-preview"]',
    content: "This week's estimated charges based on demos delivered so far. The final amount is calculated and charged on Sunday.",
    title: "This Week's Preview",
  },
  {
    target: '[data-tour-id="payment-methods"]',
    content: "Your payment methods on file. We recommend having at least 2 cards — a primary and a backup — to avoid missed charges if one fails.",
    title: "Payment Methods",
  },
  {
    target: '[data-tour-id="billing-history"]',
    content: "Complete billing history showing weekly demo charges, status (collected, pending, or failed), and amounts. Each row is one billing week.",
    title: "Billing History",
  },
];

export const salesRevenueSteps: Step[] = [
  {
    target: "body",
    content: "Sales Revenue tracks the value of deals closed through your demo pipeline. Revenue data comes from your GHL opportunities.",
    title: "Sales Revenue",
    placement: "center",
    disableBeacon: true,
  },
];

export const clientSalesSteps: Step[] = [
  {
    target: "body",
    content: "View your assigned sales closers and their performance metrics. Elite package clients can request additional closers or replacements.",
    title: "Sales Team",
    placement: "center",
    disableBeacon: true,
  },
];

export const demoLibrarySteps: Step[] = [
  {
    target: "body",
    content: "Your Demo Library contains recordings of all your demos. Filter by status (completed, no-show, closed) or search by prospect name. Click a demo to watch the recording.",
    title: "Demo Library",
    placement: "center",
    disableBeacon: true,
  },
];

export const clientReportsSteps: Step[] = [
  {
    target: "body",
    content: "Reports show your weekly performance metrics including demo completion rates, lead quality breakdown, and campaign CTR. End-of-day reports are submitted by your team daily.",
    title: "Reports",
    placement: "center",
    disableBeacon: true,
  },
];

export const supportSteps: Step[] = [
  {
    target: "body",
    content: "Submit and track support tickets here. Create a new ticket with a subject, category, and priority level. You'll be notified when your team responds.",
    title: "Support",
    placement: "center",
    disableBeacon: true,
  },
];

export const packageDetailsSteps: Step[] = [
  {
    target: "body",
    content: "View the features included in your current package. Compare with the Elite package to see what additional features are available. Request an upgrade if interested.",
    title: "Package Details",
    placement: "center",
    disableBeacon: true,
  },
];

export const attributionSteps: Step[] = [
  {
    target: "body",
    content: "Attribution tracks the complete journey from ad click to closed deal. Use the filters to scope the data, then drill into campaigns, setters, or closers.",
    title: "Attribution",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="attribution-date-range"]',
    content: "Choose a preset (7, 30, 90 days, or all time) or a custom range. All metrics on this page respect the selection.",
    title: "Date Range",
  },
  {
    target: '[data-tour-id="attribution-kpi-strip"]',
    content: "Top-line attribution totals: revenue, closed deals, show rate, and pipeline velocity. Hover any tile for its source calculation.",
    title: "Attribution KPIs",
  },
  {
    target: '[data-tour-id="attribution-tabs"]',
    content: "Switch between Campaigns (ad sources), Setters (VH team setting demos), and Closers (VH team closing deals) to see where revenue originates.",
    title: "Breakdown Tabs",
  },
  {
    target: '[data-tour-id="attribution-client-select"]',
    content: "Scope attribution to a single client, or leave on All to see the full portfolio. Non-admin roles see only their assigned clients.",
    title: "Client Scope",
  },
];

export const adminDemosSteps: Step[] = [
  {
    target: "body",
    content: "Demos & Downloads shows every demo across all clients with recording links and attribution details. Use search and filters to narrow in.",
    title: "Demos & Downloads",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="admin-demos-search"]',
    content: "Search by prospect name, email, or demo ID. Matches filter the table below in real time.",
    title: "Search",
  },
  {
    target: '[data-tour-id="admin-demos-client-filter"]',
    content: "Scope the table to one client or view demos across the entire book.",
    title: "Client Filter",
  },
  {
    target: '[data-tour-id="admin-demos-table"]',
    content: "Each row is a demo: date, prospect, client, closer, outcome, and linked recording. Expand a row to see the recording player.",
    title: "Demos List",
  },
];

export const integrationHealthSteps: Step[] = [
  {
    target: "body",
    content: "Integration Health shows daily smoke-test results for GoHighLevel, Meta, and Google Ads so you can spot drift before it breaks customer flows.",
    title: "Integration Health",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: '[data-tour-id="integration-health-summary"]',
    content: "One card per provider: last status, API version, and sunset countdown. Yellow warnings fire when a sunset is within 90 days.",
    title: "Provider Summary",
  },
  {
    target: '[data-tour-id="integration-health-run-now"]',
    content: "Trigger an ad-hoc smoke run. Read-only and idempotent — safe to click anytime.",
    title: "Manual Run",
  },
  {
    target: '[data-tour-id="integration-health-history"]',
    content: "Last 30 days of runs with latency, error snippets, and sunset flags. Hover a truncated error for the full message.",
    title: "30-Day History",
  },
];
